import types from './types';
import { createObjectCopy } from '../../../../utilities/objectUtilities';
import CreatePageConstants, { DEFAULT_CUSTOM_LAYER_SETTINGS, DEFAULT_LAYER_SPIN_SETTINGS, DEFAULT_DRIFT_SETTINGS, DEFAULT_LAYER_GLOW_SETTINGS, DEFAULT_LAYER_FIRE_SETTINGS, DEFAULT_LAYER_DROP_SHADOW_SETTINGS, DEFAULT_IMAGE_SHADOW_SETTINGS, DEFAULT_IMAGE_SIZE_FACTOR, PARTICLE_COUNT_MULTIPLIER, MAX_TOTAL_PARTICLE_COUNT } from '../CreatePageConstants';
import update from '../../../../utilities/updateStateUtilities';
import { collectionHelper } from '../../../../utilities/basicUtilities';
import { getSegmentBounds, tryPostponeBottomSegments, convertToLyricsTime, tryPostponeTopSegments } from '../../../../utilities/lyricsUtilities';
import undoable from 'redux-undo';
import { previewHistoryBatch, previewHistoryFilter } from './extensions';
import { FONT_SOURCE, TEXT_ANCHOR_POINTS, WAVE_STYLE, shakeAmountToIntensity } from '../Preview/PreviewDisplayConstants';
import { resolveFontSource } from '../../../../utilities/fontUtilities';
import { generateGuid } from '../../../../utilities/randomUtilities';

// Outline width applied to globally-styled (non-custom) layers when the global style switches to Line,
// mirroring the per-layer Line behaviour so the outline is visible (a layer's default lineWidth is 0).
const GLOBAL_LINE_OUTLINE_WIDTH = 3;

const determineFontSource = (properties, currentFontSource = FONT_SOURCE.SYSTEM) => {
    if (Object.prototype.hasOwnProperty.call(properties, 'fontSource'))
        return resolveFontSource(properties.fontSource);

    if (Object.prototype.hasOwnProperty.call(properties, 'font'))
        return FONT_SOURCE.SYSTEM;

    return resolveFontSource(currentFontSource);
};

// Video settings saved before the image elements feature have no `elements.imageElements`,
// so every read goes through this copy-on-read helper.
const getImageElements = (state) => (state.videoSettings.elements.imageElements || state.videoSettings.elements.images || []).slice();

const updateImageElements = (state, imageElements) => {
    state = update.set(state, `videoSettings.elements.imageElements`, imageElements);
    state = update.set(state, `videoSettings.elements.images`, imageElements);
    return update.set(state, `elementsSettingsUpdateTimeStamp`, Date.now());
};

const INITIAL_STATE = {
    videoSettings: createObjectCopy(CreatePageConstants.DEFAULT_VIDEO_SETTINGS),

    backgroundSettingsUpdateTimeStamp: Date.now(),
    visualizerSettingsUpdateTimeStamp: Date.now(),
    textSettingsUpdateTimeStamp: Date.now(),
    lyricsSettingsUpdateTimeStamp: Date.now(),
    elementsSettingsUpdateTimeStamp: Date.now(),
    visualizerLayersUpdateTimeStamp: Date.now(),
    lyricsSelectAllEnabled: false
}

/**
 * Normalizes background motion settings after reducer updates.
 * Ensures drift always has its default shape and resolves the mutual exclusivity
 * between background shake (Rumble) and the continuous motion modes (drift, slow zoom).
 * Rumble is mutually exclusive with both drift and slow zoom, while drift and slow
 * zoom are compatible and stack (slow zoom adds a breathing scale on top of drift).
 */
const normalizeBackgroundMotionSettings = (settings, changes = {}) => {
    const normalized = {
        ...settings,
        drift: {
            ...DEFAULT_DRIFT_SETTINGS,
            ...(settings.drift || {})
        },
        slowZoom: {
            ...(settings.slowZoom || {})
        }
    };

    normalized.drift.coverEdges = normalized.drift.coverEdges ?? true;
    normalized.drift.maxZoom = normalized.drift.maxZoom ?? 1.20;

    const driftEnabledChanged = Boolean(changes.drift && Object.prototype.hasOwnProperty.call(changes.drift, 'enabled'));
    const shakeEnabledChanged = Object.prototype.hasOwnProperty.call(changes, 'shakeEnabled');
    const slowZoomEnabledChanged = Boolean(changes.slowZoom && Object.prototype.hasOwnProperty.call(changes.slowZoom, 'enabled'));

    if (shakeEnabledChanged && normalized.shakeEnabled) {
        // Turning Rumble on disables both continuous motion modes.
        normalized.drift.enabled = false;
        normalized.slowZoom.enabled = false;
    } else {
        // Turning on drift or slow zoom disables Rumble (they conflict with shake).
        if (driftEnabledChanged && normalized.drift.enabled) {
            normalized.shakeEnabled = false;
        }
        if (slowZoomEnabledChanged && normalized.slowZoom.enabled) {
            normalized.shakeEnabled = false;
        }
        // Safety net (e.g. on load): Rumble can never coexist with drift or slow zoom.
        if (normalized.shakeEnabled && (normalized.drift.enabled || normalized.slowZoom.enabled)) {
            normalized.shakeEnabled = false;
        }
    }

    return normalized;
};

/**
 * Normalizes visualizer motion settings after reducer updates.
 * Ensures drift always has its default shape and resolves the mutual exclusivity
 * between visualizer drift and spin so only one motion mode stays enabled.
 */
const normalizeVisualizerMotionSettings = (settings, changes = {}) => {
    const normalized = {
        ...settings,
        drift: {
            ...DEFAULT_DRIFT_SETTINGS,
            ...(settings.drift || {})
        }
    };

    const driftEnabledChanged = Boolean(changes.drift && Object.prototype.hasOwnProperty.call(changes.drift, 'enabled'));
    const spinEnabledChanged = Boolean(changes.spinSettings && Object.prototype.hasOwnProperty.call(changes.spinSettings, 'enabled'));

    if (spinEnabledChanged && normalized.spinSettings?.enabled) {
        normalized.drift.enabled = false;
    } else if (driftEnabledChanged && normalized.drift.enabled) {
        normalized.spinSettings = { ...normalized.spinSettings, enabled: false };
    } else if (normalized.drift.enabled && normalized.spinSettings?.enabled) {
        normalized.spinSettings = { ...normalized.spinSettings, enabled: false };
    }

    return normalized;
};

/**
 * Normalizes particles settings after reducer updates.
 * Respects the 'enabled' property if it's already a boolean.
 * Only falls back to calculating state based on birthRate if enabled is missing (backwards compatibility).
 */
/**
 * The 2500 particle budget is shared across every type, so the per-type Count max is
 * MAX_TOTAL_PARTICLE_COUNT / typeCount. When a type is added/removed the max shrinks/grows,
 * so we clamp every stored birthRate down to the new limit - keeping the saved value in sync
 * with what the (now narrower) slider allows and the combined total within the budget.
 * Returns a new items array; only lowers values, never raises them.
 */
const clampParticleBirthRatesToBudget = (items) => {
    if (!items || items.length === 0) {
        return items;
    }

    const maxCountPerType = Math.floor(MAX_TOTAL_PARTICLE_COUNT / items.length);
    const maxBirthRate = maxCountPerType / PARTICLE_COUNT_MULTIPLIER;

    return items.map((item) => (
        item.birthRate > maxBirthRate ? { ...item, birthRate: maxBirthRate } : item
    ));
};

const normalizeParticlesSettings = (settings) => {
    if (!settings || !settings.items) {
        return settings;
    }

    const normalized = { ...settings };

    if (typeof normalized.enabled !== 'boolean') {
        normalized.enabled = settings.items.some(item => item.birthRate > 0);
    }

    return normalized;
};

const createPreviewReducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case types.SET_PREVIEW_PROPERTY: {
            return update.set(state, action.key, action.value);
        }

        case types.SET_PREVIEW_PROPERTY_WITHOUT_TRACKING: {
            return update.set(state, action.key, action.value);
        }

        case types.UPDATE_VIDEO_SETTINGS: {
            const newVideoSettings = {
                ...state.videoSettings,
                ...action.newSettings,
                visualizer: {
                    ...state.videoSettings.visualizer,
                    ...action.newSettings.visualizer,
                    // Migrate legacy documents (preset/video) that carry a discrete shakeAmount but no
                    // numeric shakeIntensity yet.
                    ...(action.newSettings.visualizer
                        && action.newSettings.visualizer.shakeIntensity === undefined
                        && action.newSettings.visualizer.shakeAmount !== undefined
                        ? { shakeIntensity: shakeAmountToIntensity(action.newSettings.visualizer.shakeAmount) }
                        : {}),
                    glowSettings: action.newSettings.visualizer ? {
                        ...state.videoSettings.visualizer.glowSettings,
                        ...action.newSettings.visualizer.glowSettings,
                    } : {
                        ...state.videoSettings.visualizer.glowSettings,
                    },
                    logoGlowSettings: action.newSettings.visualizer ? {
                        ...state.videoSettings.visualizer.logoGlowSettings,
                        ...action.newSettings.visualizer.logoGlowSettings,
                    } : {
                        ...state.videoSettings.visualizer.logoGlowSettings,
                    },
                    fireSettings: action.newSettings.visualizer ? {
                        ...state.videoSettings.visualizer.fireSettings,
                        ...action.newSettings.visualizer.fireSettings,
                    } : {
                        ...state.videoSettings.visualizer.fireSettings,
                    },
                    dropShadowSettings: action.newSettings.visualizer ? {
                        ...state.videoSettings.visualizer.dropShadowSettings,
                        ...action.newSettings.visualizer.dropShadowSettings,
                    } : {
                        ...state.videoSettings.visualizer.dropShadowSettings,
                    },
                    drift: action.newSettings.visualizer ? {
                        ...state.videoSettings.visualizer.drift,
                        ...action.newSettings.visualizer.drift,
                    } : {
                        ...state.videoSettings.visualizer.drift,
                    }
                },
                background: {
                    ...state.videoSettings.background,
                    ...action.newSettings.background,
                    hlsAdjustment: action.newSettings.background ? {
                        ...state.videoSettings.background.hlsAdjustment,
                        ...action.newSettings.background.hlsAdjustment,
                    } : {
                        ...state.videoSettings.background.hlsAdjustment,
                    },
                    drift: action.newSettings.background ? {
                        ...state.videoSettings.background.drift,
                        ...action.newSettings.background.drift,
                    } : {
                        ...state.videoSettings.background.drift,
                    },
                    videoAcceleration: action.newSettings.background ? {
                        ...state.videoSettings.background.videoAcceleration,
                        ...action.newSettings.background.videoAcceleration,
                    } : {
                        ...state.videoSettings.background.videoAcceleration,
                    },
                    slowZoom: action.newSettings.background ? {
                        ...state.videoSettings.background.slowZoom,
                        ...action.newSettings.background.slowZoom,
                    } : {
                        ...state.videoSettings.background.slowZoom,
                    }
                },
                text: {
                    ...state.videoSettings.text,
                    ...action.newSettings.text
                },
                lyrics: {
                    ...state.videoSettings.lyrics,
                    ...action.newSettings.lyrics
                },
                elements: {
                    ...state.videoSettings.elements,
                    ...action.newSettings.elements,

                    particles: action.newSettings.elements.particles
                        ? { ...state.videoSettings.elements.particles, ...action.newSettings.elements.particles }
                        : state.videoSettings.elements.particles,

                    youTubeCta: action.newSettings.elements.youTubeCta
                        ? { ...state.videoSettings.elements.youTubeCta, ...action.newSettings.elements.youTubeCta }
                        : state.videoSettings.elements.youTubeCta,

                    // A video saved before image elements existed carries an explicit null here, and
                    // that null would replace the empty list every consumer expects.
                    imageElements: action.newSettings.elements.imageElements || action.newSettings.elements.images || [],
                    images: action.newSettings.elements.imageElements || action.newSettings.elements.images || []
                }
            };

            newVideoSettings.visualizer = normalizeVisualizerMotionSettings(newVideoSettings.visualizer);
            newVideoSettings.background = normalizeBackgroundMotionSettings(newVideoSettings.background);
            newVideoSettings.elements.particles = normalizeParticlesSettings(newVideoSettings.elements.particles);
            return {
                ...state,

                videoSettings: newVideoSettings,
                backgroundSettingsUpdateTimeStamp: Date.now(),
                visualizerSettingsUpdateTimeStamp: Date.now(),
                textSettingsUpdateTimeStamp: Date.now(),
                lyricsSettingsUpdateTimeStamp: Date.now(),
                elementsSettingsUpdateTimeStamp: Date.now(),
                visualizerLayersUpdateTimeStamp: Date.now()
            }
        }

        case types.UPDATE_TEXT_ELEMENT: {
            const textElements = state.videoSettings.text.textElements.slice();
            const currentElement = textElements[action.index];
            const currentDrift = currentElement.drift || DEFAULT_DRIFT_SETTINGS;
            const updatedElement = {
                ...currentElement,
                ...action.properties,
                fontSource: determineFontSource(action.properties, currentElement.fontSource),
                position: action.properties.position ? {
                    ...currentElement.position,
                    ...action.properties.position
                } : {
                    ...currentElement.position
                },
                drift: action.properties.drift ? {
                    ...currentDrift,
                    ...action.properties.drift
                } : {
                    ...currentDrift
                },
                glowSettings: action.properties.glowSettings ? {
                    ...currentElement.glowSettings,
                    ...action.properties.glowSettings
                } : {
                    ...currentElement.glowSettings
                },
                shadowSettings: action.properties.shadowSettings ? {
                    ...currentElement.shadowSettings,
                    ...action.properties.shadowSettings
                } : {
                    ...currentElement.shadowSettings
                }
            };

            textElements.splice(action.index, 1, updatedElement);

            state = update.set(state, `videoSettings.text.textElements`, textElements);
            return update.set(state, `textSettingsUpdateTimeStamp`, Date.now());
        }

        case types.ADD_TEXT_ELEMENT: {
            const textElements = state.videoSettings.text.textElements.slice();
            const refElement = textElements[textElements.length - 1];

            const newElement = {
                "text": "NEW TEXT",
                "color": refElement ? refElement.color : "0xffffff",
                "font": refElement ? refElement.font : "Montserrat",
                "fontSource": refElement ? refElement.fontSource : FONT_SOURCE.SYSTEM,
                "fontSize": refElement ? refElement.fontSize : 25,
                "bold": refElement ? refElement.bold : false,
                "animation": refElement && refElement.animation ? refElement.animation : {
                    "enabled": false
                },
                "drift": DEFAULT_DRIFT_SETTINGS,
                "position": {
                    "x": 0,
                    "y": 0
                },
                "anchorPoint": TEXT_ANCHOR_POINTS.CENTER,
                "glowSettings": {
                    "enabled": false,
                    "blur": 15,
                    "scale": 2
                },
                "shadowSettings": {
                    "enabled": true,
                    "color": "0x000000",
                    "blur": 4,
                    "opacity": 1,
                    "distance": 4,
                    "angle": 30
                }
            };

            textElements.push(newElement);

            state = update.set(state, `videoSettings.text.textElements`, textElements);
            return update.set(state, `textSettingsUpdateTimeStamp`, Date.now());
        }

        case types.REMOVE_TEXT_ELEMENT: {
            const textElements = state.videoSettings.text.textElements.slice();
            textElements.splice(action.index, 1);

            state = update.set(state, `videoSettings.text.textElements`, textElements);
            return update.set(state, `textSettingsUpdateTimeStamp`, Date.now());
        }

        case types.DUPLICATE_TEXT_ELEMENT: {
            const textElements = state.videoSettings.text.textElements.slice();
            textElements.splice(action.index, 0, createObjectCopy(textElements[action.index]));

            state = update.set(state, `videoSettings.text.textElements`, textElements);
            return update.set(state, `textSettingsUpdateTimeStamp`, Date.now());
        }

        case types.UPDATE_BACKGROUND_SETTINGS: {
            const backgroundSettings = {
                ...state.videoSettings.background,
                ...action.properties,
                hlsAdjustment: action.properties.hlsAdjustment
                    ? { ...state.videoSettings.background.hlsAdjustment, ...action.properties.hlsAdjustment }
                    : state.videoSettings.background.hlsAdjustment,
                drift: action.properties.drift
                    ? { ...state.videoSettings.background.drift, ...action.properties.drift }
                    : state.videoSettings.background.drift,
                videoAcceleration: action.properties.videoAcceleration
                    ? { ...state.videoSettings.background.videoAcceleration, ...action.properties.videoAcceleration }
                    : state.videoSettings.background.videoAcceleration,
                slowZoom: action.properties.slowZoom
                    ? { ...state.videoSettings.background.slowZoom, ...action.properties.slowZoom }
                    : state.videoSettings.background.slowZoom,
            };

            const normalizedBackgroundSettings = normalizeBackgroundMotionSettings(backgroundSettings, action.properties);

            state = update.set(state, `videoSettings.background`, normalizedBackgroundSettings);
            return update.set(state, `backgroundSettingsUpdateTimeStamp`, Date.now());
        }

        case types.UPDATE_LYRICS_SEGMENT: {
            const segmentToUpdate = state.videoSettings.lyrics.segments[action.index];
            const updatedSegments = state.videoSettings.lyrics.segments.slice();

            updatedSegments.splice(action.index, 1, {
                ...segmentToUpdate,
                ...action.properties
            });

            const checkedSegments = updatedSegments.filter(s => Boolean(s.checked));

            if (checkedSegments.length == updatedSegments.length) {
                state = update.set(state, `lyricsSelectAllEnabled`, true);
            } else {
                state = update.set(state, `lyricsSelectAllEnabled`, false);
            }

            state = update.set(state, `videoSettings.lyrics.segments`, updatedSegments);
            return update.set(state, `lyricsSettingsUpdateTimeStamp`, Date.now());
        }

        case types.REMOVE_LYRICS_SEGMENT: {
            const index = collectionHelper(state.videoSettings.lyrics.segments).indexOf(s => s.id === action.lyricsSegment.id);
            const updatedSegments = state.videoSettings.lyrics.segments.slice();
            updatedSegments.splice(index, 1);

            state = update.set(state, `videoSettings.lyrics.segments`, updatedSegments);
            return update.set(state, `lyricsSettingsUpdateTimeStamp`, Date.now());
        }

        case types.REMOVE_LYRICS_SEGMENTS: {
            const currentSegments = [...state.videoSettings.lyrics.segments];
            let updatedSegments = [];

            action.lyricsSegments.length && currentSegments.forEach(segment => !action.lyricsSegments.includes(segment) && updatedSegments.push(segment));
            state = update.set(state, `videoSettings.lyrics.segments`, updatedSegments);
            return update.set(state, `lyricsSettingsUpdateTimeStamp`, Date.now());
        }

        case types.SET_LYRICS_SEGMENT_TIME: {
            const segmentIndex = state.videoSettings.lyrics.segments.indexOf(action.segment);
            let segments = state.videoSettings.lyrics.segments.slice();
            const segmentBounds = getSegmentBounds(segments, segmentIndex, action.audioDuration);

            let startTime = action.startTime;
            let endTime = action.endTime;
            let segmentsPostponeResult = undefined;

            // Freezed duration - when we're moving the whole segment. Otherwise, we're resizing the top or bottom segment's side
            if (action.freezedDuration) {
                // When the whole segment is out of the top bound
                if (startTime < segmentBounds.top) {
                    // Postpone the top segments if there is free space at the timeline, otherwise stop moving the segment
                    segmentsPostponeResult = tryPostponeTopSegments(segments, segmentIndex - 1, startTime, endTime);

                    if (!segmentsPostponeResult.isPostponed)
                        startTime = segmentBounds.top;

                    endTime = convertToLyricsTime(startTime + action.freezedDuration);
                }

                // When the whole segment is out of the bottom bound
                if (endTime > segmentBounds.bottom) {
                    // Postpone the bottom segments if there is free space at the timeline, otherwise stop moving the segment
                    segmentsPostponeResult = tryPostponeBottomSegments(segments, segmentIndex + 1, startTime, endTime, action.audioDuration);

                    if (!segmentsPostponeResult.isPostponed)
                        endTime = segmentBounds.bottom;

                    startTime = convertToLyricsTime(endTime - action.freezedDuration);
                }
            } else {
                // When the segment's top side is out of the bound
                if (startTime < segmentBounds.top) {
                    // Postpone the top segments if there is free space at the timeline, otherwise stop moving the start time
                    segmentsPostponeResult = tryPostponeTopSegments(segments, segmentIndex - 1, startTime, endTime);

                    if (!segmentsPostponeResult.isPostponed)
                        startTime = segmentBounds.top;
                }

                // When the segment's bottom side is out of the bound
                if (endTime > segmentBounds.bottom) {
                    // Postpone the bottom segments if there is free space at the timeline, otherwise stop resizing the end time
                    segmentsPostponeResult = tryPostponeBottomSegments(segments, segmentIndex + 1, startTime, endTime, action.audioDuration);

                    if (!segmentsPostponeResult.isPostponed)
                        endTime = segmentBounds.bottom;
                }
            }

            if (segmentsPostponeResult && segmentsPostponeResult.isPostponed)
                segments = segmentsPostponeResult.postponedSegments;

            segments.splice(segmentIndex, 1, { ...action.segment, startTime, endTime });
            state = update.set(state, `videoSettings.lyrics.segments`, segments);
            return update.set(state, `lyricsSettingsUpdateTimeStamp`, Date.now());
        }

        case types.ADD_LYRICS_SEGMENT: {
            const updatedSegments = state.videoSettings.lyrics.segments.slice();

            // If the list isn't empty we need to find an index to insert
            if (updatedSegments && updatedSegments.length) {
                const nextSegment = collectionHelper(updatedSegments).firstOrDefault((s) => s.startTime > action.newSegment.startTime);
                if (nextSegment) {
                    updatedSegments.splice(updatedSegments.indexOf(nextSegment), 0, action.newSegment);
                } else {
                    updatedSegments.push(action.newSegment);
                }
            } else {
                updatedSegments.push(action.newSegment);
            }

            state = update.set(state, `videoSettings.lyrics.segments`, updatedSegments);
            return update.set(state, `lyricsSettingsUpdateTimeStamp`, Date.now());
        }

        case types.ADD_LYRICS_SEGMENTS: {
            const currentSegments = [...state.videoSettings.lyrics.segments];
            const newSegments = action.newSegments;
            let segments = [];

            const sectionStartTime = newSegments[0].startTime;
            const sectionEndTime = newSegments[newSegments.length - 1].endTime;

            currentSegments.forEach(segment => {
                let beforeSection = segment.startTime < sectionStartTime && segment.endTime < sectionStartTime;
                let afterSection = segment.startTime > sectionEndTime && segment.endTime > sectionEndTime;

                if (beforeSection || afterSection) {
                    segments.push(segment)
                }
            });

            segments = [...segments, ...newSegments];
            segments.sort((a, b) => { return a.startTime - b.startTime })

            state = update.set(state, `videoSettings.lyrics.segments`, segments);
            return update.set(state, `lyricsSettingsUpdateTimeStamp`, Date.now());
        }

        case types.CLEAR_LYRICS: {
            state = update.set(state, `videoSettings.lyrics.segments`, []);
            return update.set(state, `lyricsSettingsUpdateTimeStamp`, Date.now());
        }

        case types.UPDATE_PARTICLE_ITEM: {
            const items = state.videoSettings.elements.particles.items.slice();
            items[action.index] = { ...items[action.index], ...action.properties };

            const updatedParticles = normalizeParticlesSettings({ ...state.videoSettings.elements.particles, items });
            state = update.set(state, `videoSettings.elements.particles`, updatedParticles);
            return update.set(state, `elementsSettingsUpdateTimeStamp`, Date.now());
        }

        case types.ADD_PARTICLE_ITEM: {
            const items = state.videoSettings.elements.particles.items.slice();

            const newParticleItem = {
                "shape": "circle",
                "color": "0xfbfbfb",
                "maxOpacity": 1,
                "maxSize": 0.3,
                "minOpacity": 0.2,
                "minSize": 0.1,
                "mediaData": null,
                "birthRate": 37.5,
                "flickering": {
                    "enabled": false,
                    "reactive": false,
                    "minIntensity": 0.2199,
                    "maxIntensity": 0.3142
                },
                "rotationEnabled": false
            };

            items.push(newParticleItem);
            const updatedParticles = normalizeParticlesSettings({
                ...state.videoSettings.elements.particles,
                items: clampParticleBirthRatesToBudget(items),
                enabled: true
            });
            state = update.set(state, `videoSettings.elements.particles`, updatedParticles);
            return update.set(state, `elementsSettingsUpdateTimeStamp`, Date.now());
        }

        case types.REMOVE_PARTICLE_ITEM: {
            const items = state.videoSettings.elements.particles.items.slice();
            items.splice(action.index, 1);

            const updatedParticles = normalizeParticlesSettings({ ...state.videoSettings.elements.particles, items: clampParticleBirthRatesToBudget(items) });
            state = update.set(state, `videoSettings.elements.particles`, updatedParticles);
            return update.set(state, `elementsSettingsUpdateTimeStamp`, Date.now());
        }

        case types.DUPLICATE_PARTICLE_ITEM: {
            const items = state.videoSettings.elements.particles.items.slice();
            items.splice(action.index, 0, createObjectCopy(items[action.index]));

            const updatedParticles = normalizeParticlesSettings({ ...state.videoSettings.elements.particles, items: clampParticleBirthRatesToBudget(items) });
            state = update.set(state, `videoSettings.elements.particles`, updatedParticles);
            return update.set(state, `elementsSettingsUpdateTimeStamp`, Date.now());
        }

        case types.UPDATE_IMAGE_ELEMENT: {
            const imageElements = getImageElements(state);
            const currentElement = imageElements[action.index];

            imageElements[action.index] = {
                ...currentElement,
                ...action.properties,
                position: action.properties.position
                    ? { ...currentElement.position, ...action.properties.position }
                    : currentElement.position,
                shadowSettings: action.properties.shadowSettings
                    ? { ...currentElement.shadowSettings, ...action.properties.shadowSettings }
                    : currentElement.shadowSettings
            };

            return updateImageElements(state, imageElements);
        }

        case types.ADD_IMAGE_ELEMENT: {
            const imageElements = getImageElements(state);

            imageElements.push({
                "id": generateGuid(),
                "mediaSource": action.mediaSource,
                "visible": true,
                "position": action.properties?.position ? { ...action.properties.position } : {
                    "x": 0,
                    "y": 0
                },
                "sizeFactor": action.properties?.sizeFactor ?? DEFAULT_IMAGE_SIZE_FACTOR,
                "shadowSettings": action.properties?.shadowSettings
                    ? { ...action.properties.shadowSettings }
                    : { ...DEFAULT_IMAGE_SHADOW_SETTINGS }
            });

            return updateImageElements(state, imageElements);
        }

        case types.REMOVE_IMAGE_ELEMENT: {
            const imageElements = getImageElements(state);
            imageElements.splice(action.index, 1);

            return updateImageElements(state, imageElements);
        }

        case types.DUPLICATE_IMAGE_ELEMENT: {
            const imageElements = getImageElements(state);
            const duplicatedElement = createObjectCopy(imageElements[action.index]);
            duplicatedElement.id = generateGuid();
            imageElements.splice(action.index, 0, duplicatedElement);

            return updateImageElements(state, imageElements);
        }

        case types.CHANGE_IMAGE_ELEMENT_ORDER: {
            const imageElements = getImageElements(state);
            [imageElements[action.firstIndex], imageElements[action.secondIndex]] =
                [imageElements[action.secondIndex], imageElements[action.firstIndex]];

            return updateImageElements(state, imageElements);
        }

        case types.UPDATE_LYRICS_STYLE: {
            const { glowSettings, shadowSettings, drift, position, ...basicStyle } = action.properties;

            return {
                ...state,
                videoSettings: {
                    ...state.videoSettings,
                    lyrics: {
                        ...state.videoSettings.lyrics,
                        style: {
                            ...state.videoSettings.lyrics.style,
                            ...basicStyle,
                            fontSource: determineFontSource(action.properties, state.videoSettings.lyrics.style.fontSource),
                            position: position
                                ? { ...state.videoSettings.lyrics.style.position, ...position }
                                : state.videoSettings.lyrics.style.position,
                            shadowSettings: shadowSettings
                                ? { ...state.videoSettings.lyrics.style.shadowSettings, ...shadowSettings }
                                : state.videoSettings.lyrics.style.shadowSettings,
                            glowSettings: glowSettings
                                ? { ...state.videoSettings.lyrics.style.glowSettings, ...glowSettings }
                                : state.videoSettings.lyrics.style.glowSettings,
                        },
                        drift: drift
                            ? { ...state.videoSettings.lyrics.drift, ...drift }
                            : state.videoSettings.lyrics.drift
                    }
                },
                lyricsSettingsUpdateTimeStamp: Date.now()
            };
        }

        case types.ADD_WAVE_LAYER: {
            const waveLayers = state.videoSettings.visualizer.waveCircles.slice();

            let fillColor = "0x" + Math.floor(Math.random() * 16777215).toString(16);
            for (let i = fillColor.length; i < 8; i++) {
                fillColor += '0';
            }

            // A fresh layer defaults to no outline. Under the global Line style (outline-only, no fill)
            // that would render it invisible, so mirror the global Line side-effect: give the new layer
            // a visible outline coloured from its own fill.
            const isLineStyle = state.videoSettings.visualizer.waveStyle === WAVE_STYLE.LINE;

            const newWaveLayer = {
                "id": generateGuid(),
                "fillColor": fillColor,
                "fillAlpha": 1,
                "lineWidth": isLineStyle ? GLOBAL_LINE_OUTLINE_WIDTH : 0,
                "lineColor": isLineStyle ? fillColor : '0x000000',
                "lineAlpha": 1,
                "visible": true,
                "customOptions": {
                    ...DEFAULT_CUSTOM_LAYER_SETTINGS
                }
            };

            waveLayers.splice(0, 0, newWaveLayer);
            return updateVisualizerLayers(state, waveLayers);
        }

        case types.UPDATE_WAVE_LAYER: {
            const waveLayers = state.videoSettings.visualizer.waveCircles.slice();
            const { glowSettings, fireSettings, dropShadowSettings } = state.videoSettings.visualizer;

            const currentLayer = waveLayers[action.index];
            const currentCustomOptions = currentLayer.customOptions || {};

            let newGlowSettings = action.properties.glowSettings
                ? { ...currentCustomOptions.glowSettings, ...action.properties.glowSettings }
                : currentCustomOptions.glowSettings;

            if (action.properties.glowSettings?.enabled === true) {
                newGlowSettings = { ...glowSettings, ...action.properties.glowSettings };
            }

            let newFireSettings = action.properties.fireSettings
                ? { ...currentCustomOptions.fireSettings, ...action.properties.fireSettings }
                : currentCustomOptions.fireSettings;

            if (action.properties.fireSettings?.enabled === true) {
                newFireSettings = { ...fireSettings, ...action.properties.fireSettings };
            }

            let newDropShadowSettings = action.properties.dropShadowSettings
                ? { ...currentCustomOptions.dropShadowSettings, ...action.properties.dropShadowSettings }
                : currentCustomOptions.dropShadowSettings;

            if (action.properties.dropShadowSettings?.enabled === true) {
                newDropShadowSettings = { ...dropShadowSettings, ...action.properties.dropShadowSettings };
            }

            const { glowSettings: propGlow, fireSettings: propFire, dropShadowSettings: propShadow, ...otherProps } = action.properties;

            const updatedLayer = {
                ...currentLayer,
                ...otherProps,
                customOptions: {
                    ...currentCustomOptions,
                    glowSettings: newGlowSettings,
                    fireSettings: newFireSettings,
                    dropShadowSettings: newDropShadowSettings
                }
            };
            waveLayers.splice(action.index, 1, updatedLayer);

            state = update.set(state, `videoSettings.visualizer.waveCircles`, waveLayers);
            return update.set(state, `visualizerSettingsUpdateTimeStamp`, Date.now());
        }

        case types.UPDATE_WAVE_LAYER_SPIN_SETTINGS: {
            const waveLayers = state.videoSettings.visualizer.waveCircles.slice();
            const currentLayer = waveLayers[action.index];

            const updatedLayer = {
                ...currentLayer,
                customOptions: {
                    ...currentLayer.customOptions,
                    spinSettings: {
                        ...currentLayer.customOptions.spinSettings,
                        [action.key]: action.value
                    }
                }
            };

            waveLayers.splice(action.index, 1, updatedLayer);

            state = update.set(state, `videoSettings.visualizer.waveCircles`, waveLayers);
            return update.set(state, `visualizerSettingsUpdateTimeStamp`, Date.now());
        }

        case types.UPDATE_WAVE_LAYER_CUSTOM_SETTINGS: {
            const waveLayers = state.videoSettings.visualizer.waveCircles.slice();
            const {
                rotation,
                waveType,
                reflectionType,
                smoothed,
                inverted,
                waveStyle,
                waveScaleFactor,
                barCount,
                barWidth,
                pointRadius,
                movementDirection
            } = state.videoSettings.visualizer;

            const currentLayer = waveLayers[action.index];
            const isInitialized = currentLayer.customOptions.isInitialized;

            let updatedCustomOptions = {
                ...currentLayer.customOptions,
                [action.key]: action.value
            };

            if (action.key === "enabled" && !isInitialized) {
                updatedCustomOptions = {
                    rotation,
                    waveType,
                    reflectionType,
                    enabled: action.value,
                    smoothed,
                    inverted,
                    waveStyle,
                    waveScaleFactor,
                    barCount,
                    barWidth,
                    pointRadius,
                    movementDirection, // inherit the global movement direction as the layer default
                    hollowCenter: false,
                    // Transparent is a top-level control (lives above Customize and applies independently),
                    // so initializing Customize must NOT reset it — keep whatever the user already set.
                    cutout: currentLayer.customOptions.cutout ?? false,
                    isInitialized: true,
                    glowSettings: currentLayer.customOptions?.glowSettings || DEFAULT_LAYER_GLOW_SETTINGS,
                    fireSettings: currentLayer.customOptions?.fireSettings || DEFAULT_LAYER_FIRE_SETTINGS,
                    dropShadowSettings: currentLayer.customOptions?.dropShadowSettings || DEFAULT_LAYER_DROP_SHADOW_SETTINGS,
                    spinSettings: currentLayer.customOptions?.spinSettings || DEFAULT_LAYER_SPIN_SETTINGS
                };
            }

            let updatedLayer = {
                ...currentLayer,
                customOptions: updatedCustomOptions
            };

            // A layer's lineWidth/lineColor auto-track whichever style actually drives it: the custom
            // style while Customize is on, the global style once it is off. Toggling Customize swaps
            // which style is active, so reconcile the Line outline across that swap the same way the
            // global-style (UPDATE_VISUALIZER_SETTINGS) and per-layer-style handlers do. Without this,
            // turning Customize off while the global style is Line leaves a stale lineWidth of 0, so the
            // outline-only Line renders empty and the layer disappears from the preview (and the export).
            //
            // The "previous" style must come from the PRE-UPDATE layer (what it was actually rendering).
            // customOptions.isInitialized is not persisted to Cosmos, so after a save/reopen the init
            // branch above rewrites updatedCustomOptions.waveStyle to the global style; reading the
            // "previous" style from there would mask a Solid->Line swap on disable and the layer vanishes.
            if (action.key === "enabled") {
                const previousActiveWaveStyle = currentLayer.customOptions.enabled
                    ? currentLayer.customOptions.waveStyle
                    : waveStyle;
                const nextActiveWaveStyle = action.value ? updatedCustomOptions.waveStyle : waveStyle;

                if (nextActiveWaveStyle === WAVE_STYLE.LINE && previousActiveWaveStyle !== WAVE_STYLE.LINE)
                    updatedLayer = { ...updatedLayer, lineWidth: GLOBAL_LINE_OUTLINE_WIDTH, lineColor: updatedLayer.fillColor };
                else if (nextActiveWaveStyle !== WAVE_STYLE.LINE && previousActiveWaveStyle === WAVE_STYLE.LINE)
                    updatedLayer = { ...updatedLayer, lineWidth: 0, lineColor: '0x000000' };
            }

            waveLayers.splice(action.index, 1, updatedLayer);

            state = update.set(state, `videoSettings.visualizer.waveCircles`, waveLayers);

            // When every layer's spectrum has converged on the same value that differs from the
            // global one, promote that value to the global spectrum. Done in the same state update
            // so the per-layer and global change undo together. Skipped for a single layer.
            if (action.key === "waveType" && waveLayers.length >= 2) {
                const consensusWaveType = waveLayers[0].customOptions.waveType;
                const allLayersAgree = waveLayers.every(layer => layer.customOptions.waveType === consensusWaveType);

                if (allLayersAgree && consensusWaveType !== state.videoSettings.visualizer.waveType) {
                    state = update.set(state, `videoSettings.visualizer.waveType`, consensusWaveType);
                }
            }

            return update.set(state, `visualizerSettingsUpdateTimeStamp`, Date.now());
        }

        case types.REMOVE_WAVE_LAYER: {
            const waveLayers = state.videoSettings.visualizer.waveCircles.slice();

            if (action.index !== undefined)
                waveLayers.splice(action.index, 1)
            else
                waveLayers.splice(0, 1)

            return updateVisualizerLayers(state, waveLayers);
        }

        case types.DUPLICATE_WAVE_LAYER: {
            const waveLayers = state.videoSettings.visualizer.waveCircles.slice();
            waveLayers.splice(action.index, 0, createObjectCopy(waveLayers[action.index]));
            return updateVisualizerLayers(state, waveLayers);
        }

        case types.CHANGE_WAVE_LAYER_ORDER: {
            const waveLayers = state.videoSettings.visualizer.waveCircles.slice();
            [waveLayers[action.firstIndex], waveLayers[action.secondIndex]] = [{ ...waveLayers[action.secondIndex] }, { ...waveLayers[action.firstIndex] }];
            return updateVisualizerLayers(state, waveLayers);
        }

        case types.UPDATE_VISUALIZER_SETTINGS: {
            const previousWaveStyle = state.videoSettings.visualizer.waveStyle;

            const visualizerSettings = {
                ...state.videoSettings.visualizer,
                ...action.properties,

                position: action.properties.position
                    ? { ...state.videoSettings.visualizer.position, ...action.properties.position }
                    : state.videoSettings.visualizer.position,

                spinSettings: action.properties.spinSettings
                    ? { ...state.videoSettings.visualizer.spinSettings, ...action.properties.spinSettings }
                    : state.videoSettings.visualizer.spinSettings,

                glowSettings: action.properties.glowSettings
                    ? { ...state.videoSettings.visualizer.glowSettings, ...action.properties.glowSettings }
                    : state.videoSettings.visualizer.glowSettings,

                logoGlowSettings: action.properties.logoGlowSettings
                    ? { ...state.videoSettings.visualizer.logoGlowSettings, ...action.properties.logoGlowSettings }
                    : state.videoSettings.visualizer.logoGlowSettings,

                fireSettings: action.properties.fireSettings
                    ? { ...state.videoSettings.visualizer.fireSettings, ...action.properties.fireSettings }
                    : state.videoSettings.visualizer.fireSettings,

                dropShadowSettings: action.properties.dropShadowSettings
                    ? { ...state.videoSettings.visualizer.dropShadowSettings, ...action.properties.dropShadowSettings }
                    : state.videoSettings.visualizer.dropShadowSettings,

                drift: action.properties.drift
                    ? { ...state.videoSettings.visualizer.drift, ...action.properties.drift }
                    : state.videoSettings.visualizer.drift
            };

            const normalizedVisualizerSettings = normalizeVisualizerMotionSettings(visualizerSettings, action.properties);

            state = update.set(state, `videoSettings.visualizer`, normalizedVisualizerSettings);

            // "Line" is an outline-only style. When the GLOBAL style switches to or from Line, mirror the
            // per-layer Line behaviour on the layers the global style actually drives (non-custom layers):
            // give them a visible outline coloured from their own fill on enable, and reset it on disable.
            // Applied in the same update so it undoes together with the style change.
            const nextWaveStyle = normalizedVisualizerSettings.waveStyle;
            if (nextWaveStyle !== previousWaveStyle && (nextWaveStyle === WAVE_STYLE.LINE || previousWaveStyle === WAVE_STYLE.LINE)) {
                const enablingLine = nextWaveStyle === WAVE_STYLE.LINE;
                const updatedLayers = state.videoSettings.visualizer.waveCircles.map(layer => {
                    if (layer.customOptions && layer.customOptions.enabled) return layer;
                    return enablingLine
                        ? { ...layer, lineWidth: GLOBAL_LINE_OUTLINE_WIDTH, lineColor: layer.fillColor }
                        : { ...layer, lineWidth: 0, lineColor: '0x000000' };
                });
                state = update.set(state, `videoSettings.visualizer.waveCircles`, updatedLayers);
            }

            return update.set(state, `visualizerSettingsUpdateTimeStamp`, Date.now());
        }

        case types.SELECT_OR_DESELECT_SEGMENTS: {
            const updatedSegments = state.videoSettings.lyrics.segments.slice();

            for (var i = 0; i < updatedSegments.length; i++) {
                const segment = updatedSegments[i];

                if (segment.checked !== action.lyricsSelectAllEnabled) {
                    updatedSegments[i] = {
                        ...segment,
                        checked: action.lyricsSelectAllEnabled
                    };
                }
            }

            state = update.set(state, `videoSettings.lyrics.segments`, updatedSegments);
            return update.set(state, `lyricsSelectAllEnabled`, action.lyricsSelectAllEnabled);
        }

        case types.UPDATE_VISUALIZER_REFLECTION_TYPE: {
            const reflectionType = action.reflectionType;
            const barCount = action.barCount;

            return {
                ...state,
                videoSettings: {
                    ...state.videoSettings,
                    visualizer: {
                        ...state.videoSettings.visualizer,
                        barCount,
                        reflectionType,
                    },
                },
                visualizerSettingsUpdateTimeStamp: Date.now()
            };
        }

        case types.UPDATE_VISUALIZER_LAYERS_TYPE: {
            const { layoutType } = action;

            return {
                ...state,
                videoSettings: {
                    ...state.videoSettings,
                    visualizer: {
                        ...state.videoSettings.visualizer,
                        layoutType
                    },
                },
                visualizerSettingsUpdateTimeStamp: Date.now(),
                visualizerLayersUpdateTimeStamp: Date.now(),
            };
        }

        case types.UPDATE_ELEMENTS_SETTINGS: {
            const elementsSettings = {
                ...state.videoSettings.elements,
                ...action.properties,

                particles: action.properties.particles
                    ? {
                        ...state.videoSettings.elements.particles,
                        ...action.properties.particles,
                        flock: action.properties.particles.flock
                            ? { ...state.videoSettings.elements.particles.flock, ...action.properties.particles.flock }
                            : state.videoSettings.elements.particles.flock
                    }
                    : state.videoSettings.elements.particles,

                youTubeCta: action.properties.youTubeCta
                    ? { ...state.videoSettings.elements.youTubeCta, ...action.properties.youTubeCta }
                    : state.videoSettings.elements.youTubeCta
            };

            state = update.set(state, 'videoSettings.elements', elementsSettings);
            return update.set(state, 'elementsSettingsUpdateTimeStamp', Date.now());
        }

        case types.UPDATE_ASPECT_RATIO: {
            state = update.set(state, `backgroundSettingsUpdateTimeStamp`, Date.now());
            return update.set(state, 'videoSettings.aspectRatio', action.aspectRatio);
        }

        case types.RESET_VIDEO_SETTINGS: {
            return {
                ...state,
                videoSettings: createObjectCopy(CreatePageConstants.DEFAULT_VIDEO_SETTINGS),
            }
        }

        default: return state;
    }
}

const updateVisualizerLayers = (state, waveLayers) => {
    return {
        ...state,
        videoSettings: {
            ...state.videoSettings,
            visualizer: {
                ...state.videoSettings.visualizer,
                waveCircles: waveLayers,
            },
        },
        visualizerLayersUpdateTimeStamp: Date.now(),
        visualizerSettingsUpdateTimeStamp: Date.now()
    };
}

export default undoable(createPreviewReducer, {
    limit: 30,
    ignoreInitialState: false,
    undoType: types.UNDO,
    redoType: types.REDO,
    clearHistoryType: types.CLEAR_ACTIONS_HISTORY,
    filter: previewHistoryFilter.init(),
    groupBy: previewHistoryBatch.init()
});